/**
 * AdminPromisesPage — iter1117
 *
 * Админ панел за прегледа на всички ангажименти Nutramilo. Позволява
 * филтриране по статус и ръчно отбелязване на обработено
 * възстановяване (референтен номер от Mollie касата).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "",          label: "Всички",      color: "#0EA5E9" },
  { id: "active",    label: "Активни",     color: "#0EA5E9" },
  { id: "achieved",  label: "Постигнати",  color: "#10B981" },
  { id: "failed",    label: "Непостигнати", color: "#EF4444" },
  { id: "cancelled", label: "Отказани",    color: "#64748B" },
  { id: "refunded",  label: "Възстановени", color: "#8B5CF6" },
];

const STATUS_COLOR = {
  active:    "#0EA5E9",
  achieved:  "#10B981",
  failed:    "#EF4444",
  cancelled: "#64748B",
  refunded:  "#8B5CF6",
};

export default function AdminPromisesPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [tab, setTab] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [refRef, setRefRef] = useState({});

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/nutramilo-promise/admin/list`, {
        params: status ? { status } : {},
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Неуспешно зареждане.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const markRefunded = async (promiseId) => {
    const reference = (refRef[promiseId] || "").trim();
    if (!reference) {
      toast.error("Моля, въведете референтен номер.");
      return;
    }
    setWorkingId(promiseId);
    try {
      await axios.post(
        `${API}/nutramilo-promise/admin/${promiseId}/mark-refunded`,
        { reference }
      );
      toast.success("Отбелязано като възстановено.");
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка.");
    } finally {
      setWorkingId(null);
    }
  };

  const bg = isDark ? "#0B0F19" : "#F8FAFC";
  const card = isDark ? "#111827" : "#FFFFFF";
  const text = isDark ? "#E2E8F0" : "#0F172A";
  const muted = isDark ? "#94A3B8" : "#475569";
  const border = isDark ? "#1F2937" : "#E2E8F0";

  return (
    <div style={{ background: bg, minHeight: "100vh", color: text, paddingBottom: 60 }} data-testid="admin-promises-page">
      <MobileTopBar />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>
          Ангажименти Nutramilo (админ)
        </h1>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          {TABS.map((t) => (
            <button
              key={t.id || "all"}
              onClick={() => setTab(t.id)}
              data-testid={`promises-tab-${t.id || "all"}`}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 13,
                border: `1px solid ${tab === t.id ? t.color : border}`,
                background: tab === t.id ? t.color + "22" : "transparent",
                color: tab === t.id ? t.color : muted,
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 60, display: "flex", justifyContent: "center" }}>
            <Loader2 className="animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: muted }}>
            Няма записи в тази категория.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`promise-row-${it.id}`}
                style={{
                  background: card,
                  border: `1px solid ${border}`,
                  borderRadius: 10,
                  padding: 14,
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
                    <strong>{it.user_email || it.user_id}</strong>
                    <span style={{
                      padding: "2px 8px", borderRadius: 999, fontSize: 11,
                      background: (STATUS_COLOR[it.status] || "#64748B") + "22",
                      color: STATUS_COLOR[it.status] || "#64748B",
                    }}>
                      {it.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: muted }}>
                    Включен: {new Date(it.started_at).toLocaleDateString("bg-BG")}
                    {" · "}
                    Изтича: {new Date(it.ends_at).toLocaleDateString("bg-BG")}
                    {it.settled_at && <> · Приключи: {new Date(it.settled_at).toLocaleDateString("bg-BG")}</>}
                    {it.refunded_at && <> · Възстановен: {new Date(it.refunded_at).toLocaleDateString("bg-BG")}</>}
                    {it.refund_reference && <> · Реф.: {it.refund_reference}</>}
                  </div>
                  <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>
                    Цели: {it.target_percent}% пълнота, {it.target_weeks} седмици ·
                    Награда: {it.refund_amount} {it.refund_currency}
                  </div>
                </div>

                {it.status === "achieved" && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="Референтен номер"
                      value={refRef[it.id] || ""}
                      onChange={(e) => setRefRef({ ...refRef, [it.id]: e.target.value })}
                      data-testid={`promise-ref-input-${it.id}`}
                      style={{
                        padding: "6px 10px",
                        border: `1px solid ${border}`,
                        borderRadius: 6,
                        fontSize: 13,
                        width: 180,
                        background: bg,
                        color: text,
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={() => markRefunded(it.id)}
                      disabled={workingId === it.id}
                      data-testid={`promise-mark-refunded-${it.id}`}
                    >
                      <CheckCircle2 size={14} style={{ marginRight: 4 }} />
                      Отбележи
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
